/**
 * Install the default Trusted Types policy. MUST be called before
 * React mounts, because React 18's hydration path uses `innerHTML`
 * on the root element, which throws under a Trusted Types CSP if no
 * default policy exists.
 *
 * The policy passes input through verbatim — this does NOT add XSS
 * protection on its own. What it DOES is surface any unexpected
 * innerHTML / eval / script-URL sink by throwing a visible TypeError
 * in the console. If a compromised dependency ever tried to write
 * raw strings to one of those sinks, the Trusted Types runtime
 * would catch it loudly instead of silently executing.
 */
export function installTrustedTypes(): void {
  if (typeof window === 'undefined') return;
  const tt = (
    window as unknown as {
      trustedTypes?: {
        createPolicy(
          name: string,
          rules: {
            createHTML?: (s: string) => string;
            createScript?: (s: string) => string;
            createScriptURL?: (s: string) => string;
          },
        ): unknown;
      };
    }
  ).trustedTypes;
  if (!tt) return;
  try {
    tt.createPolicy('default', {
      createHTML: (s) => s,
      createScript: (s) => s,
      createScriptURL: (s) => s,
    });
  } catch {
    // Policy already installed (HMR or duplicate mount). Safe to ignore.
  }
}
